import { Editor, EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { IconButton } from './joy/IconButton';
import { Option } from './joy/Option';
import { Select } from './joy/Select';
import { Stack } from './joy/Stack';
import { Sheet } from './joy/Sheet';
import { Divider } from './joy/Divider';
import { ToggleButtonGroup } from './joy/ToggleButtonGroup';
import { useTheme } from './joy/Theme';
import { compact } from 'lodash-es';
import { CSSProperties } from 'react';
import { Trans } from '@lingui/react';

const extensions = [StarterKit.configure({ heading: { levels: [1, 2, 3] } }), Underline];

export const RichTextInput = ({
  onChange,
  value,
  style = {},
}: {
  value?: string | null;
  onChange?: (html: string) => void;
  style?: CSSProperties | null;
}) => {
  const editor = useEditor({ extensions, onUpdate: ev => onChange?.(ev.editor.getHTML()), content: value });
  const theme = useTheme();
  return (
    <Sheet
      sx={{
        p: 1,
        borderRadius: 8,
        borderColor: theme.palette.divider,
        borderStyle: 'solid',
        borderWidth: 1,
      }}
    >
      <MenuBar editor={editor} />
      <EditorContent editor={editor} style={{ backgroundColor: theme.palette.background.surface, ...style }} />
    </Sheet>
  );
};

const BoldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 11H12.5C13.8807 11 15 9.88071 15 8.5C15 7.11929 13.8807 6 12.5 6H8V11ZM18 15.5C18 17.9853 15.9853 20 13.5 20H6V4H12.5C14.9853 4 17 6.01472 17 8.5C17 9.70431 16.5269 10.7981 15.7564 11.6058C17.0979 12.3847 18 13.837 18 15.5ZM8 13V18H13.5C14.8807 18 16 16.8807 16 15.5C16 14.1193 14.8807 13 13.5 13H8Z"></path>
  </svg>
);

const ItalicIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15 20H7V18H9.92661L12.0425 6H9V4H17V6H14.0734L11.9575 18H15V20Z"></path>
  </svg>
);

const UnderlineIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 3V12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12V3H18V12C18 15.3137 15.3137 18 12 18C8.68629 18 6 15.3137 6 12V3H8ZM4 20H20V22H4V20Z"></path>
  </svg>
);

const BulletListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 4H21V6H8V4ZM4.5 6.5C3.67157 6.5 3 5.82843 3 5C3 4.17157 3.67157 3.5 4.5 3.5C5.32843 3.5 6 4.17157 6 5C6 5.82843 5.32843 6.5 4.5 6.5ZM4.5 13.5C3.67157 13.5 3 12.8284 3 12C3 11.1716 3.67157 10.5 4.5 10.5C5.32843 10.5 6 11.1716 6 12C6 12.8284 5.32843 13.5 4.5 13.5ZM4.5 20.4C3.67157 20.4 3 19.7284 3 18.9C3 18.0716 3.67157 17.4 4.5 17.4C5.32843 17.4 6 18.0716 6 18.9C6 19.7284 5.32843 20.4 4.5 20.4ZM8 11H21V13H8V11ZM8 18H21V20H8V18Z"></path>
  </svg>
);

const OrderedListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M5.75024 3.5H4.71733L3.25 3.89317V5.44582L4.25002 5.17782L4.25018 8.5H3V10H7V8.5H5.75024V3.5ZM10 4H21V6H10V4ZM10 11H21V13H10V11ZM10 18H21V20H10V18ZM2.875 15.625C2.875 14.4514 3.82639 13.5 5 13.5C6.17361 13.5 7.125 14.4514 7.125 15.625C7.125 16.1106 6.96183 16.5587 6.68747 16.9167L6.68271 16.9229L5.31587 18.5H7V20H3.00012L2.99959 18.8786L5.4717 16.035C5.5673 15.9252 5.625 15.7821 5.625 15.625C5.625 15.2798 5.34518 15 5 15C4.67378 15 4.40573 15.2501 4.37747 15.5688L4.3651 15.875H2.875V15.625Z"></path>
  </svg>
);

const MenuBar = ({ editor }: { editor: Editor | null }) => {
  // const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  const activeText = editor.isActive('heading', { level: 1 })
    ? 'h1'
    : editor.isActive('heading', { level: 2 })
      ? 'h2'
      : editor.isActive('heading', { level: 3 })
        ? 'h3'
        : editor.isActive('paragraph')
          ? 'p'
          : null;

  const activeMarks = compact([
    editor.isActive('bold') ? 'bold' : null,
    editor.isActive('italic') ? 'italic' : null,
    editor.isActive('underline') ? 'underline' : null,
  ]);

  const activeLists = compact([
    editor.isActive('bulletList') ? 'bulletList' : null,
    editor.isActive('orderedList') ? 'orderedList' : null,
  ]);

  return (
    <Stack gap={1}>
      <Stack direction="row" gap={1}>
        <Select value={activeText} sx={{ minWidth: 120 }}>
          <Option value="p" onClick={() => editor.chain().focus().setParagraph().run()}>
            <Trans id="Paragraph" />
          </Option>
          <Option value="h1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
            <Trans id="Heading 1" />
          </Option>
          <Option value="h2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
            <Trans id="Heading 2" />
          </Option>
          <Option value="h3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
            <Trans id="Heading 3" />
          </Option>
        </Select>
        <ToggleButtonGroup size="sm" value={activeMarks}>
          <IconButton value="bold" onClick={() => editor.chain().focus().toggleBold().run()}>
            <BoldIcon />
          </IconButton>
          <IconButton value="italic" onClick={() => editor.chain().focus().toggleItalic().run()}>
            <ItalicIcon />
          </IconButton>
          <IconButton value="underline" onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <UnderlineIcon />
          </IconButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup size="sm" value={activeLists}>
          <IconButton value="orderedList" onClick={() => editor.chain().focus().toggleOrderedList().run()}>
            <OrderedListIcon />
          </IconButton>
          <IconButton value="bulletList" onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <BulletListIcon />
          </IconButton>
        </ToggleButtonGroup>
      </Stack>
      <Divider />
    </Stack>
  );
};
